<template>
  <!-- ?prelander=1 (functions/prelander.js). Nothing here is tappable: it moves to JOIN by itself, and the
       only control is the close cross in the header. No Evina cptpl_* markers: they belong to the JOIN page. -->
  <section class="prelander" :aria-busy="!ready">
    <div class="prelander__spacer" aria-hidden="true"></div>

    <div class="prelander__block">
      <p class="prelander__eyebrow"><span class="prelander__dot" aria-hidden="true"></span>{{ t.prelander.eyebrow }}</p>
      <h1 class="prelander__headline" :style="headlineStyle">{{ t.prelander.headline[0] }}<br />{{ t.prelander.headline[1] }}</h1>
      <p class="prelander__subline">{{ t.prelander.subline }}</p>
      <div class="prelander__track" aria-hidden="true">
        <!-- Fills only while the page is loaded and on screen, in step with the time that counts. -->
        <div class="prelander__fill" :class="{ 'prelander__fill--running': counting }" :style="{ animationDuration: `${PRELANDER_MIN_MS}ms` }"></div>
      </div>
      <div class="prelander__status" role="status">
        <span :key="phase" class="prelander__status-line">{{ t.prelander.status[phase] }}</span>
      </div>
    </div>

    <div class="prelander__spacer" aria-hidden="true"></div>
    <!-- Same wording as the price line under JOIN: the price is seen twice before consent. -->
    <p class="price-line">{{ t.priceLine }}</p>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useBrandStore } from '@/stores/brandStore'
import { useSubscriptionStore, Steps } from '@/stores/subscriptionStore'
import { PRELANDER_MIN_MS, PRELANDER_MAX_MS, PRELANDER_STATUS_MS, PRELANDER_READY_BEAT_MS, PRELANDER_LOAD_WAIT_MS } from '@/functions/prelander'
import riskCheck from '@/functions/riskCheck'
import resolveVariant from '@/functions/variant'
import { isMockMode } from '@/services/telkomPremiumService'
import { complianceProtectTest } from '@/functions/evina'

const { brandConfig, translations: t } = storeToRefs(useBrandStore())
const store = useSubscriptionStore()
const { step } = storeToRefs(store)
const router = useRouter()
const route = useRoute()

// JOIN replaces this screen only when all of these hold (business, 19 Oct 2026):
//  1. our fraud check has run and found nothing (functions/riskCheck.js; a hit goes to the error page at once),
//  2. the page has loaded: the art decoded and the fonts ready, so the price shows as designed,
//  3. the visitor can see it: the tab is in front and the browser has painted it,
//  4. it has been on screen like that for PRELANDER_MIN_MS (time in a background tab does not count),
// and, so JOIN does not open on a spinner, the visitor has been identified (waited for up to PRELANDER_MAX_MS
// of on-screen time). The Evina kit is only requested here; it goes in with JOIN (SubscribePanel), because
// ComplianceProtect snapshots the page it is injected into (8101 / 8102 over this screen, 19 Oct 2026).
const checked = ref(false)
const loaded = ref(false)
const pageVisible = ref(document.visibilityState === 'visible')
const painted = ref(false)
const counting = computed(() => checked.value && loaded.value && pageVisible.value && painted.value)
const ready = computed(() => step.value !== Steps.Checking)
const shownMs = ref(0)
// Status line 0, 1, then 2 ("Ready.") once the visitor is identified; all on on-screen time.
const phase = ref(0)

// Sized at the 390px design width like the landing headline, scaling down with the column.
const headlineStyle = computed(() => {
  const { headlineSize: size, headlineLeading } = brandConfig.value.prelander
  return { fontSize: `min(${size}px, calc((100vw - 40px) * ${(size / 350).toFixed(4)}))`, lineHeight: headlineLeading }
})

const startedAt = performance.now()
const since = () => Math.round(performance.now() - startedAt)
const timers = []
const later = (ms, fn) => timers.push(setTimeout(fn, ms))
const timing = { identifiedMs: null, readyMs: null, loadedMs: null, shownMs: 0 }
let readyShownAt = null
let lastTick = performance.now()
let finished = false

// A frame actually drawn while the tab is in front (requestAnimationFrame does not run in a background tab).
function confirmPainted() {
  painted.value = false
  if (!pageVisible.value) return
  requestAnimationFrame(() => requestAnimationFrame(() => (painted.value = pageVisible.value)))
}

function onVisibility() {
  pageVisible.value = document.visibilityState === 'visible'
  confirmPainted()
}

// Art decoded and fonts ready, or the ceiling passed: the price text is on screen either way.
function waitForLoad() {
  const art = brandConfig.value.variants[resolveVariant(brandConfig.value)]?.background
  const image = art
    ? new Promise((resolve) => {
        const img = new Image()
        img.onload = img.onerror = resolve
        img.src = art
      })
    : Promise.resolve()
  const fonts = document.fonts?.ready ?? Promise.resolve()
  const ceiling = new Promise((resolve) => later(PRELANDER_LOAD_WAIT_MS, resolve))
  Promise.race([Promise.all([image, fonts]), ceiling]).then(() => {
    if (timing.loadedMs === null) timing.loadedMs = since()
    loaded.value = true
  })
}

function tick() {
  const now = performance.now()
  if (counting.value) shownMs.value += now - lastTick
  lastTick = now
  if (finished) return

  const shown = shownMs.value
  if (phase.value === 0 && shown >= PRELANDER_STATUS_MS[0]) phase.value = 1
  if (phase.value === 1 && shown >= PRELANDER_STATUS_MS[1] && ready.value) {
    phase.value = 2
    readyShownAt = shown
  }
  if (phase.value === 2 && shown >= Math.max(PRELANDER_MIN_MS, readyShownAt + PRELANDER_READY_BEAT_MS)) return finish(false)
  // Still not identified after this much on-screen time: JOIN's own spinner covers the rest.
  if (!ready.value && shown >= PRELANDER_MAX_MS) finish(true)
}

function finish(capped) {
  if (finished) return
  finished = true
  store.endPrelander({ ...timing, shownMs: Math.round(shownMs.value), advancedMs: since(), capped })
}

// Flagged by our fraud check: the error page at once, instead of JOIN, so no Evina transaction is started.
function block(reasons) {
  finished = true
  store.blockPrelander(reasons, { ...timing, shownMs: Math.round(shownMs.value), advancedMs: since(), capped: false })
  router.replace({ name: 'error', query: route.query })
}

watch(step, (value) => {
  if (value !== Steps.Checking && timing.identifiedMs === null) timing.identifiedMs = since()
})

watch(ready, (value) => {
  if (value && timing.readyMs === null) timing.readyMs = since()
})

onMounted(() => {
  // Our own check runs first (live pages and Evina's review, not mock QA); it never asks Evina.
  const risk = !isMockMode() || complianceProtectTest() ? riskCheck() : { blocked: false, reasons: [] }
  if (risk.blocked) return block(risk.reasons)
  checked.value = true

  store.startPrelander(brandConfig.value)
  document.addEventListener('visibilitychange', onVisibility)
  confirmPainted()
  waitForLoad()
  timers.push(setInterval(tick, 50))
})

onBeforeUnmount(() => {
  timers.forEach((id) => {
    clearTimeout(id)
    clearInterval(id)
  })
  document.removeEventListener('visibilitychange', onVisibility)
})
</script>

<style scoped>
.prelander {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.prelander__spacer {
  flex: 1;
  min-height: 24px;
}

.prelander__block {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.prelander__eyebrow {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.3;
  letter-spacing: .12em;
  text-transform: uppercase;
  color: var(--prelander-accent);
}

.prelander__dot {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  background: var(--prelander-accent);
  animation: prelander-pulse 1s ease-in-out infinite;
}

.prelander__headline {
  font-family: var(--headline-font);
  font-weight: 700;
  letter-spacing: var(--headline-tracking);
  text-transform: var(--headline-transform);
  color: #fff;
}

.prelander__subline {
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.8);
}

/* The bar loads across in the two seconds the page is on screen; it holds while the tab is hidden. */
.prelander__track {
  margin-top: 14px;
  height: 6px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.16);
  overflow: hidden;
}

.prelander__fill {
  width: 0;
  height: 100%;
  border-radius: 9999px;
  background: var(--ladder-fill);
  animation: prelander-fill 2s cubic-bezier(.4, 0, .2, 1) forwards paused;
}

.prelander__fill--running {
  animation-play-state: running;
}

.prelander__status {
  height: 18px;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: rgba(255, 255, 255, 0.65);
}

/* Each line rises in as it replaces the last (CSS only, so a paused tab cannot hold the text back). */
.prelander__status-line {
  display: block;
  animation: prelander-line-in 160ms ease-out both;
}

@keyframes prelander-line-in {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: none; }
}

@keyframes prelander-fill {
  from { width: 0; }
  to { width: 100%; }
}

@keyframes prelander-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: .35; transform: scale(.6); }
}
</style>
